import React, { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Users,
  CheckCircle2,
  XCircle,
  Clock,
  QrCode,
  Smartphone,
  UserCheck,
  TrendingUp,
  Calendar,
  RefreshCw,
  Search,
  Download,
  Loader2,
} from 'lucide-react'
import { attendanceService, type ChurchService, type AttendanceRecord, type ServiceAttendanceStats } from '../../services/attendanceService'
import { useSubscription } from '@/hooks/useSubscription'
import { FeatureRestricted } from '@/components/subscription/FeatureRestricted'

// Temporary auth hook
const useAuth = () => {
  const [user] = useState({
    churchId: localStorage.getItem('churchId') || '',
    id: localStorage.getItem('userId') || ''
  })
  return { user }
}

export default function AttendanceDashboard() {
  console.log('[AttendanceDashboard] Component rendering')
  const { user } = useAuth()

  // Check subscription access
  const { features, limits, loading: subscriptionLoading } = useSubscription(user.churchId)
  const [selectedService, setSelectedService] = useState<ChurchService | null>(null)
  const [todaysServices, setTodaysServices] = useState<ChurchService[]>([])
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([])
  const [stats, setStats] = useState<ServiceAttendanceStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [autoRefresh, setAutoRefresh] = useState(true)

  const loadAttendanceData = useCallback(async () => {
    console.log('[AttendanceDashboard] loadAttendanceData called')
    if (!selectedService || !user?.churchId) {
      console.log('[AttendanceDashboard] loadAttendanceData - missing selectedService or churchId')
      return
    }

    try {
      console.log('[AttendanceDashboard] loadAttendanceData - fetching data')
      const [records, serviceStats] = await Promise.all([
        attendanceService.getServiceAttendance(selectedService.id),
        attendanceService.getServiceStats(selectedService.id, user.churchId)
      ])
      console.log('[AttendanceDashboard] loadAttendanceData - data fetched')
      setAttendanceRecords(records)
      setStats(serviceStats)
    } catch (error) {
      console.error('[AttendanceDashboard] Error loading attendance data:', error)
    }
  }, [selectedService, user?.churchId])

  const loadTodaysServices = useCallback(async () => {
    console.log('[AttendanceDashboard] loadTodaysServices - starting')
    if (!user?.churchId) {
      console.log('[AttendanceDashboard] loadTodaysServices - no churchId')
      setLoading(false)
      return
    }

    try {
      console.log('[AttendanceDashboard] loadTodaysServices - fetching services')
      const services = await attendanceService.getTodaysServices(user.churchId)
      console.log('[AttendanceDashboard] loadTodaysServices - services fetched:', services.length)
      setTodaysServices(services)
      if (services.length > 0 && !selectedService) {
        console.log('[AttendanceDashboard] loadTodaysServices - setting first service')
        setSelectedService(services[0])
      }
      setLoading(false)
    } catch (error) {
      console.error('[AttendanceDashboard] Error loading today\'s services:', error)
      setLoading(false)
    }
  }, [user?.churchId, selectedService])

  useEffect(() => {
    console.log('[AttendanceDashboard] useEffect - loadTodaysServices called')
    loadTodaysServices()
  }, [loadTodaysServices])

  useEffect(() => {
    console.log('[AttendanceDashboard] useEffect - selectedService changed:', selectedService?.id)
    if (selectedService) {
      loadAttendanceData()
    }
  }, [selectedService, loadAttendanceData])

  useEffect(() => {
    console.log('[AttendanceDashboard] useEffect - autoRefresh setup:', autoRefresh)
    if (!autoRefresh || !selectedService) return

    const interval = setInterval(() => {
      console.log('[AttendanceDashboard] autoRefresh - calling loadAttendanceData')
      loadAttendanceData()
    }, 10000) // Refresh every 10 seconds

    return () => {
      console.log('[AttendanceDashboard] autoRefresh - cleaning up interval')
      clearInterval(interval)
    }
  }, [autoRefresh, selectedService, loadAttendanceData])

  const handleManualRefresh = () => {
    loadAttendanceData()
  }

  const filteredRecords = attendanceRecords.filter(record =>
    record.member?.preferred_name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    })
  }

  const getCheckInSourceBadge = (source?: string) => {
    const variants: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' }> = {
      qr_scan: { label: 'QR Scan', variant: 'default' },
      manual_app: { label: 'Mobile App', variant: 'default' },
      manual_admin: { label: 'Admin', variant: 'secondary' },
      post_service: { label: 'Post-Service', variant: 'outline' },
    }
    const config = variants[source || ''] || { label: 'Unknown', variant: 'outline' }
    return <Badge variant={config.variant}>{config.label}</Badge>
  }

  if (loading || subscriptionLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  // Check if attendance feature is enabled
  if (!features.attendance) {
    return (
      <FeatureRestricted
        featureName="Attendance Tracking"
        description="Track service attendance with QR codes, mobile check-ins, and comprehensive analytics."
        currentPlan={limits?.plan || 'starter'}
        requiredPlan="growth"
        icon={<UserCheck className="h-10 w-10 text-white" />}
      />
    )
  }

  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Attendance Dashboard</h1>
          <p className="text-muted-foreground">Real-time attendance monitoring</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Checkbox
              id="auto-refresh"
              checked={autoRefresh}
              onCheckedChange={(checked) => setAutoRefresh(checked as boolean)}
            />
            <Label htmlFor="auto-refresh" className="text-sm font-normal cursor-pointer">
              Auto-refresh
            </Label>
          </div>
          <Button onClick={handleManualRefresh} variant="outline" size="sm">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button variant="outline" size="sm">
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Service Selector */}
      {todaysServices.length > 0 ? (
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-2">
              <Label htmlFor="service-select">Select Service</Label>
              <Select
                value={selectedService?.id || ''}
                onValueChange={(value) => {
                  const service = todaysServices.find(s => s.id === value)
                  setSelectedService(service || null)
                }}
              >
                <SelectTrigger id="service-select" className="w-full max-w-md">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {todaysServices.map(service => (
                    <SelectItem key={service.id} value={service.id}>
                      {service.service_name} - {service.service_time}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No services scheduled for today</p>
          </CardContent>
        </Card>
      )}

      {selectedService && stats && (
        <>
          {/* Stats Cards */}
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Expected</CardTitle>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total_expected}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Checked In</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.total_attended}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Absent</CardTitle>
                <XCircle className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">{stats.total_absent}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Attendance Rate</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{stats.attendance_rate}%</div>
              </CardContent>
            </Card>
          </div>

          {/* Check-in Methods Stats */}
          <Card>
            <CardHeader>
              <CardTitle>Check-in Methods</CardTitle>
              <CardDescription>Breakdown by check-in source</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <QrCode className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">QR Scan</p>
                    <p className="text-lg font-semibold">{stats.check_in_sources.qr_scan}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <Smartphone className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Mobile App</p>
                    <p className="text-lg font-semibold">{stats.check_in_sources.manual_app}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <UserCheck className="h-5 w-5 text-sky-500" />
                  <div>
                    <p className="text-sm text-muted-foreground">Admin</p>
                    <p className="text-lg font-semibold">{stats.check_in_sources.manual_admin}</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 p-3 border rounded-lg">
                  <Clock className="h-5 w-5 text-yellow-600" />
                  <div>
                    <p className="text-sm text-muted-foreground">Post-Service</p>
                    <p className="text-lg font-semibold">{stats.check_in_sources.post_service}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Attendance Records Table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Attendance Records</CardTitle>
                  <CardDescription>Members who have checked in</CardDescription>
                </div>
                <div className="relative w-64">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search members..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Member</TableHead>
                    <TableHead>Check-in Time</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRecords.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        {searchTerm ? 'No members found matching your search.' : 'No check-ins yet.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredRecords.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {record.member?.photo_url ? (
                              <img
                                src={record.member.photo_url}
                                alt={record.member.preferred_name}
                                className="w-8 h-8 rounded-full"
                              />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <span className="text-xs font-medium">
                                  {record.member?.preferred_name?.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            )}
                            <div>
                              <p className="font-medium">{record.member?.preferred_name}</p>
                              <p className="text-xs text-muted-foreground">{record.member?.phone_number}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{formatTime(record.check_in_time)}</TableCell>
                        <TableCell className="capitalize">{record.check_in_method}</TableCell>
                        <TableCell>{getCheckInSourceBadge(record.check_in_source)}</TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {record.notes || '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>

              <div className="mt-4 pt-4 border-t text-center text-muted-foreground text-sm">
                Showing {filteredRecords.length} of {attendanceRecords.length} check-ins
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
